import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Typography, Tag, Spin, Empty } from 'antd';
import Footer from '../../Routes/Footer';
import PublicHeader from '../../Routes/PublicHeader';
import { useApi } from '../../hooks/use_api';
import { useNodeInfo } from '../../hooks/use_node_info';

const { Title, Paragraph, Text } = Typography;

/**
 * Public list of this node's consents — the same wording every user is asked
 * to respond to on sign-in. Consents are defined per node (in Node settings),
 * so each node's /consents shows its own. Readable before sign-in, like the
 * privacy and trust pages that link here.
 */
function ConsentsPage() {
    const api = useApi();
    const { nodeId } = useNodeInfo();
    const [consents, setConsents] = useState(null); // null = loading
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        api.get('/api/consents/public')
            .then(data => setConsents(Array.isArray(data) ? data : []))
            .catch(() => { setConsents([]); setFailed(true); });
    }, [api]);

    return (
      <>
        <PublicHeader />
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '32px 16px 64px' }}>
            <Title level={3}>Consents{nodeId ? ` on ${nodeId}` : ''}</Title>
            <Paragraph type="secondary">
                When you sign in to this node, you're asked to respond to the items
                below. Required ones must be accepted to use the service; optional ones
                are your choice. If any wording changes, you'll be asked again. See also
                our <Link to="/privacy">Privacy Policy</Link>.
            </Paragraph>

            {consents === null ? (
                <Spin style={{ display: 'block', margin: '32px auto' }} />
            ) : consents.length === 0 ? (
                <Empty
                    style={{ margin: '32px 0' }}
                    description={failed
                        ? "Couldn't load this node's consents. Please try again later."
                        : "This node doesn't ask for any consents."}
                />
            ) : consents.map(c => (
                <div key={c.id} style={{ marginTop: 24 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <Title level={4} style={{ margin: 0 }}>{c.title}</Title>
                        {c.required ? <Tag color="red">Required</Tag> : <Tag>Optional</Tag>}
                        <Text type="secondary" style={{ fontSize: 12 }}>Version {c.version}</Text>
                    </div>
                    <Paragraph style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>{c.body}</Paragraph>
                </div>
            ))}
            <Footer />
        </div>
      </>
    );
}

export default ConsentsPage;
